import { supabase } from '@/integrations/supabase/client';

export interface SessionBooking {
  id?: string;
  booking_id?: string;
  mentor_id: string;
  mentee_id: string;
  application_id?: string;
  booked_date: string;
  start_time: string;
  end_time: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  notes?: string;
  subscription_booking_id?: string;
}

export interface SessionBookingWithMentee extends SessionBooking {
  id: string;
  mentee: {
    first_name: string;
    last_name: string;
    avatar_url?: string;
  };
  is_subscription_active?: boolean;
  subscription_end_date?: string;
  course_type?: string;
  subscription_type?: string;
  subscription_booking_id?: string;
  application_text_id?: string; // The text application ID like "APP-20250823-001"
}

export const getSessionBookings = async (mentorId: string) => {
  try {
    const { data, error } = await supabase
      .from('session_bookings')
      .select(`
        *,
        applications!session_bookings_application_id_fkey (
          application_id,
          is_subscription_active,
          subscription_end_date,
          courses!applications_course_id_fkey (
            course_type,
            subscription_type
          )
        )
      `)
      .eq('mentor_id', mentorId)
      .order('booked_date');

    if (error) throw error;
    
    if (!data || data.length === 0) {
      return { success: true, data: [] };
    }

    // Get unique mentee IDs and fetch their profiles
    const uniqueMenteeIds = [...new Set(data.map(booking => booking.mentee_id))];
    const { data: menteeProfiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, first_name, last_name, avatar_url')
      .in('id', uniqueMenteeIds);

    if (profilesError) throw profilesError;
    
    // Type cast and flatten the data to match our interface
    const typedData = data?.map(booking => {
      const menteeProfile = menteeProfiles?.find(p => p.id === booking.mentee_id);
      return {
        ...booking,
        status: booking.status as 'pending' | 'confirmed' | 'cancelled' | 'completed',
        // Keep the original UUID application_id, add text version separately
        application_text_id: booking.applications?.application_id,
        mentee: menteeProfile ? {
          first_name: menteeProfile.first_name,
          last_name: menteeProfile.last_name,
          avatar_url: menteeProfile.avatar_url
        } : {
          first_name: '',
          last_name: '',
          avatar_url: ''
        },
        is_subscription_active: booking.applications?.is_subscription_active,
        subscription_end_date: booking.applications?.subscription_end_date,
        course_type: booking.applications?.courses?.course_type,
        subscription_type: booking.applications?.courses?.subscription_type
      };
    }) as SessionBookingWithMentee[];
    
    return { success: true, data: typedData };
  } catch (error) {
    console.error('Error fetching session bookings:', error);
    return { success: false, error };
  }
};

export const createSessionBooking = async (booking: SessionBooking) => {
  try {
    const { data, error } = await supabase
      .from('session_bookings')
      .insert(booking)
      .select()
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error creating session booking:', error);
    return { success: false, error };
  }
};

export const updateSessionBookingStatus = async (id: string, status: SessionBooking['status']) => {
  try {
    const { error } = await supabase
      .from('session_bookings')
      .update({ status })
      .eq('id', id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error updating session booking:', error);
    return { success: false, error };
  }
};